import type { Omittable } from "@salestrak/ui";

import type { Option } from "#app/components/automations/trigger-sentence";

// The ONE place an `Option[]` is turned into headed rows. Both of `FieldSelect`'s
// render paths consume this — `optionsToPool` for the searchable
// (EntitySelector) path and `GroupedOptions` for the plain `Select` one — so the
// two cannot render a list in different orders. They each used to carry their own
// copy of the level-1 bucketing.
//
// Two levels: `Option.group` is the section eyebrow, `Option.subgroup` the
// indented sub-eyebrow beneath it. The Action dropdown uses them as source →
// action category (SalesTrak) or provider → connection (an integration).

export type OptionRow = Omittable<{
  option: Option;
  section?: string;
  subsection?: string;
}>;

// Buckets options into contiguous (section, subsection) runs. First-appearance
// order at both levels, with the un-labelled members last at both levels:
// grouped sections in the order their first member appears then the ungrouped
// tail, and within a section its subgroups in the same fashion then that
// section's un-subgrouped members.
//
// It never SORTS — the caller's array order decides which section comes first
// and how rows read within a run. Bucketing only makes each run contiguous, and
// that is load-bearing rather than cosmetic: `EntitySelectorPool` opens a heading
// whenever the (sub)section differs from the previous row's, and keys collapse
// state by the label. So two separated runs of one subgroup render two headings
// that share a single collapse key, and folding either one hides the other's
// rows.
export function groupOptionRows(options: Option[]): OptionRow[] {
  type Bucket = { order: string[]; bySub: Map<string, Option[]>; bare: Option[] };
  const newBucket = (): Bucket => ({ order: [], bySub: new Map(), bare: [] });

  const order: string[] = [];
  const byGroup = new Map<string, Bucket>();
  const ungrouped = newBucket();

  for (const option of options) {
    if (!option.group) {
      // A subsection is indented UNDER a section, so with no section there is
      // nothing for it to nest beneath: an ungrouped option is a plain tail row
      // whatever else it declares.
      ungrouped.bare.push(option);
      continue;
    }
    let bucket = byGroup.get(option.group);
    if (!bucket) {
      bucket = newBucket();
      order.push(option.group);
      byGroup.set(option.group, bucket);
    }
    if (!option.subgroup) {
      bucket.bare.push(option);
      continue;
    }
    const forSub = bucket.bySub.get(option.subgroup);
    if (forSub) {
      forSub.push(option);
    } else {
      bucket.order.push(option.subgroup);
      bucket.bySub.set(option.subgroup, [option]);
    }
  }

  const rows: OptionRow[] = [];
  const emit = (bucket: Bucket, section?: string) => {
    for (const subsection of bucket.order) {
      // Seeded with its first member above, so the entry is present.
      for (const option of bucket.bySub.get(subsection) ?? []) {
        rows.push({ option, section, subsection });
      }
    }
    for (const option of bucket.bare) rows.push({ option, section });
  };
  // A section was seeded for every key in `order`, so the lookup resolves.
  for (const section of order) emit(byGroup.get(section) ?? newBucket(), section);
  emit(ungrouped);
  return rows;
}

// Consecutive rows sharing one heading, in order. A `Select` popup needs the runs
// rather than the flat rows, because a heading there is a wrapper element around
// its items instead of a row of its own. Since `groupOptionRows` makes each
// (section, subsection) pair contiguous, comparing against the previous row is
// enough to find every run.
export function runsBy(
  rows: OptionRow[],
  key: "section" | "subsection",
): OptionRow[][] {
  const runs: OptionRow[][] = [];
  for (const row of rows) {
    const last = runs.at(-1);
    if (last && last[0]?.[key] === row[key]) last.push(row);
    else runs.push([row]);
  }
  return runs;
}
